import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import styles from "./debugSupportPanel.module.css";

const PANEL_ENTRY_LIMIT = 10;

function formatTimestamp(timestamp) {
  if (!timestamp) {
    return "—";
  }

  return new Date(timestamp).toLocaleString();
}

function buildEntriesText(entries) {
  return entries
    .map(entry => {
      const status = entry.status != null ? entry.status : "—";
      return `${entry.requestId || "—"} | ${entry.method} ${entry.url} | ${status} | ${formatTimestamp(entry.timestamp)}`;
    })
    .join("\n");
}

const DebugSupportPanel = () => {
  const entries = useSelector(state => state.requestTracking.entries);
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const wasOpenRef = useRef(false);

  const recentEntries = useMemo(() => entries.slice(0, PANEL_ENTRY_LIMIT), [entries]);
  const lastEntry = recentEntries[0];

  const copyText = useCallback(async text => {
    if (!text) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Failed to copy request tracking data", error);
    }
  }, []);

  const handleCopyLastId = useCallback(() => {
    copyText(lastEntry?.requestId || "");
  }, [copyText, lastEntry?.requestId]);

  const handleCopyAll = useCallback(() => {
    copyText(buildEntriesText(recentEntries));
  }, [copyText, recentEntries]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setIsOpen(current => !current);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // The panel is not modal: the page behind it stays usable, so focus is moved in on open and back to the toggle on
  // close, but never trapped. Escape closes it only while focus is inside it.
  useEffect(() => {
    if (isOpen) {
      panelRef.current?.querySelector("button")?.focus();
    } else if (wasOpenRef.current) {
      toggleRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handlePanelKeyDown = event => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        ref={toggleRef}
        type="button"
        className={styles.toggleButton}
        onClick={() => setIsOpen(true)}
        aria-label="Open request debug panel"
        title="Open request debug panel (Ctrl+Shift+D)"
      >
        ⚙
      </button>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div ref={panelRef} className={styles.panel} role="dialog" aria-label="Request debug panel" onKeyDown={handlePanelKeyDown}>
      <div className={styles.header}>
        <h2 className={styles.title}>Request debug</h2>
        <button type="button" className={styles.closeButton} onClick={() => setIsOpen(false)} aria-label="Close panel">
          ×
        </button>
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.actionButton} onClick={handleCopyLastId} disabled={!lastEntry?.requestId}>
          Copy last ID
        </button>
        <button type="button" className={styles.actionButton} onClick={handleCopyAll} disabled={!recentEntries.length}>
          Copy all 10 as text
        </button>
      </div>

      <ul className={styles.entryList}>
        {recentEntries.length ? (
          recentEntries.map(entry => (
            <li key={`${entry.requestId}-${entry.timestamp}`} className={styles.entryItem}>
              <code className={styles.entryId}>{entry.requestId || "—"}</code>
              <span className={styles.entryMeta}>
                {entry.method} {entry.url}
              </span>
              <span className={styles.entryMeta}>
                {entry.status != null ? entry.status : "—"} · {formatTimestamp(entry.timestamp)}
              </span>
            </li>
          ))
        ) : (
          <li className={styles.emptyState}>No tracked requests yet.</li>
        )}
      </ul>
    </div>
  );
};

export default DebugSupportPanel;
